import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Flame, Eye, Heart, MessageCircle, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { api } from "@/lib/api";

function TrendIcon({ trend }) {
    if (trend === "up") return <TrendingUp className="h-3.5 w-3.5 text-emerald-500" strokeWidth={2.8} />;
    if (trend === "down") return <TrendingDown className="h-3.5 w-3.5 text-rose-500" strokeWidth={2.8} />;
    return <Minus className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={2.8} />;
}

function StatCard({ icon: Icon, label, value, sub, onClick, testId, accent = false, badge }) {
    const Wrap = onClick ? motion.button : motion.div;
    return (
        <Wrap
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={onClick}
            data-testid={testId}
            className={`relative rounded-3xl border p-4 text-left lift overflow-hidden ${
                accent ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card"
            }`}
        >
            <div className="flex items-center justify-between">
                <div className={`h-8 w-8 rounded-xl grid place-items-center ${accent ? "bg-white/20" : "bg-accent text-accent-foreground"}`}>
                    <Icon className="h-4 w-4" strokeWidth={2.5} />
                </div>
                {badge != null && badge > 0 && !accent && (
                    <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-black grid place-items-center">
                        {badge}
                    </span>
                )}
            </div>
            <div className="mt-3 flex items-baseline gap-1.5">
                <div className={`font-display text-2xl font-black tracking-tighter ${accent ? "" : ""}`}>{value}</div>
                {sub}
            </div>
            <div className={`mt-0.5 text-xs ${accent ? "opacity-90" : "text-muted-foreground"}`}>{label}</div>
        </Wrap>
    );
}

export default function StatsCard({ user }) {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        const load = async () => {
            try {
                const { data } = await api.get("/stats/me");
                if (alive) setStats(data);
            } catch (e) {
                // silent
            } finally {
                if (alive) setLoading(false);
            }
        };
        load();
        const t = setInterval(load, 8000);
        return () => {
            alive = false;
            clearInterval(t);
        };
    }, []);

    if (loading && !stats) {
        return (
            <div className="mt-6 rounded-3xl border border-border bg-card p-6 animate-pulse text-muted-foreground text-sm" data-testid="stats-loading">
                Loading your dashboard…
            </div>
        );
    }

    const s = stats || {};
    return (
        <div className="mt-6" data-testid="stats-card">
            <div className="grid grid-cols-2 gap-3">
                <StatCard
                    icon={Heart}
                    label="Top match %"
                    value={`${s.avg_match_percent ?? 0}%`}
                    sub={<TrendIcon trend={s.match_trend} />}
                    accent
                    testId="stat-match-percent"
                />
                <StatCard
                    icon={Flame}
                    label="Day streak"
                    value={s.streak_days ?? 0}
                    testId="stat-streak"
                />
                <StatCard
                    icon={Eye}
                    label="Profile views · week"
                    value={s.profile_views_week ?? 0}
                    testId="stat-views"
                />
                <StatCard
                    icon={Heart}
                    label="Active matches"
                    value={s.active_matches ?? 0}
                    onClick={() => navigate("/app/chat")}
                    testId="stat-matches"
                />
            </div>
            <button
                onClick={() => navigate("/app/chat")}
                data-testid="stat-messages-btn"
                className="mt-3 w-full rounded-3xl border border-border bg-card p-4 flex items-center gap-3 text-left lift"
            >
                <div className="h-10 w-10 rounded-xl bg-accent text-accent-foreground grid place-items-center">
                    <MessageCircle className="h-5 w-5" strokeWidth={2.5} />
                </div>
                <div className="flex-1">
                    <div className="font-display font-bold">
                        {s.unread_messages > 0 ? `${s.unread_messages} new message${s.unread_messages > 1 ? "s" : ""}` : "Your chats"}
                    </div>
                    <div className="text-xs text-muted-foreground">
                        {s.unread_messages > 0 ? "Someone's waiting for a reply" : "See all mutual matches"}
                    </div>
                </div>
                {s.unread_messages > 0 && (
                    <span className="min-w-[24px] h-6 px-2 rounded-full bg-primary text-primary-foreground text-xs font-black grid place-items-center" data-testid="stat-unread-badge">
                        {s.unread_messages}
                    </span>
                )}
            </button>
        </div>
    );
}
